import React, { useState, useEffect } from "react";
import universityService from "../../services/universityService";
import campusService from "../../services/campusService";
import facultyService from "../../services/facultyService";

const DepartmentForm = ({ department, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    universityId: "",
    campusId: "",
    facultyId: "",
    nameEn: "",
    nameLocal: "",
    status: true,
  });

  const [universities, setUniversities] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [universityLoading, setUniversityLoading] = useState(false);
  const [campusLoading, setCampusLoading] = useState(false);
  const [facultyLoading, setFacultyLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // University search states
  const [universitySearchTerm, setUniversitySearchTerm] = useState("");
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
  const [universitySearchTimeout, setUniversitySearchTimeout] = useState(null);

  // Campus search states
  const [campusSearchTerm, setCampusSearchTerm] = useState("");
  const [filteredCampuses, setFilteredCampuses] = useState([]);
  const [showCampusDropdown, setShowCampusDropdown] = useState(false);
  const [campusSearchTimeout, setCampusSearchTimeout] = useState(null);

  // Load form data when department prop changes
  useEffect(() => {
    if (department) {
      setFormData({
        universityId: department.universityId || "",
        campusId: department.campusId || "",
        facultyId: department.facultyId || "",
        nameEn: department.nameEn || "",
        nameLocal: department.nameLocal || "",
        status: department.status !== undefined ? department.status : true,
      });

      // Set search terms for display
      if (department.universityName) {
        setUniversitySearchTerm(department.universityName);
      }
      if (department.campusName) {
        setCampusSearchTerm(department.campusName);
      }

      // Load data based on existing selections
      if (department.universityId) {
        loadCampusesByUniversity(department.universityId);
        if (department.campusId) {
          loadFaculties(department.campusId);
        }
      }
    } else {
      setFormData({
        universityId: "",
        campusId: "",
        facultyId: "",
        nameEn: "",
        nameLocal: "",
        status: true,
      });
      setUniversitySearchTerm("");
      setCampusSearchTerm("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [department]);

  // Load universities on component mount
  useEffect(() => {
    loadUniversities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load campuses when university is selected
  useEffect(() => {
    if (formData.universityId) {
      loadCampusesByUniversity(formData.universityId);
    } else {
      setCampuses([]);
      setFilteredCampuses([]);
      setFormData((prev) => ({ ...prev, campusId: "", facultyId: "" }));
      setCampusSearchTerm("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.universityId]);

  // Load faculties when campus is selected
  useEffect(() => {
    if (formData.campusId) {
      loadFaculties(formData.campusId);
    } else {
      setFaculties([]);
      setFormData((prev) => ({ ...prev, facultyId: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.campusId]);

  // University search effect
  useEffect(() => {
    if (universitySearchTimeout) {
      clearTimeout(universitySearchTimeout);
    }

    const timeout = setTimeout(() => {
      filterUniversities();
    }, 300);

    setUniversitySearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universitySearchTerm, universities]);

  // Campus search effect
  useEffect(() => {
    if (campusSearchTimeout) {
      clearTimeout(campusSearchTimeout);
    }

    const timeout = setTimeout(() => {
      filterCampuses();
    }, 300);

    setCampusSearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusSearchTerm, campuses]);

  const loadUniversities = async () => {
    try {
      setUniversityLoading(true);
      const response = await universityService.getUniversities(0, 1000);
      if (response.success) {
        const sortedUniversities = (response.data.data || []).sort((a, b) =>
          (a.nameLocal || a.nameEn || "").localeCompare(
            b.nameLocal || b.nameEn || ""
          )
        );
        setUniversities(sortedUniversities);
        setFilteredUniversities(sortedUniversities);
      }
    } catch (error) {
      console.error("Error loading universities:", error);
    } finally {
      setUniversityLoading(false);
    }
  };

  const loadCampusesByUniversity = async (universityId) => {
    try {
      setCampusLoading(true);
      const response = await campusService.getCampuses(
        0,
        1000,
        "",
        universityId
      );
      if (response.success) {
        const sortedCampuses = (response.data.data || []).sort((a, b) =>
          (a.nameLocal || a.nameEn || "").localeCompare(
            b.nameLocal || b.nameEn || ""
          )
        );
        setCampuses(sortedCampuses);
        setFilteredCampuses(sortedCampuses);
      }
    } catch (error) {
      console.error("Error loading campuses:", error);
      setCampuses([]);
      setFilteredCampuses([]);
    } finally {
      setCampusLoading(false);
    }
  };

  const loadFaculties = async (campusId) => {
    try {
      setFacultyLoading(true);
      const response = await facultyService.getFaculties(0, 1000, "", campusId);
      if (response.success) {
        setFaculties(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading faculties:", error);
      setFaculties([]);
    } finally {
      setFacultyLoading(false);
    }
  };

  const filterUniversities = () => {
    if (!universitySearchTerm.trim()) {
      setFilteredUniversities(universities);
      return;
    }

    const filtered = universities.filter(
      (university) =>
        (university.nameEn || "")
          .toLowerCase()
          .includes(universitySearchTerm.toLowerCase()) ||
        (university.nameLocal || "")
          .toLowerCase()
          .includes(universitySearchTerm.toLowerCase())
    );
    setFilteredUniversities(filtered);
  };

  const filterCampuses = () => {
    if (!campusSearchTerm.trim()) {
      setFilteredCampuses(campuses);
      return;
    }

    const filtered = campuses.filter(
      (campus) =>
        (campus.nameEn || "")
          .toLowerCase()
          .includes(campusSearchTerm.toLowerCase()) ||
        (campus.nameLocal || "")
          .toLowerCase()
          .includes(campusSearchTerm.toLowerCase())
    );
    setFilteredCampuses(filtered);
  };

  const handleUniversitySelect = (university) => {
    setFormData((prev) => ({
      ...prev,
      universityId: university.id,
      campusId: "",
      facultyId: "",
    }));
    setUniversitySearchTerm(university.nameEn);
    setShowUniversityDropdown(false);
    setCampusSearchTerm("");

    // Clear university error if any
    if (errors.universityId) {
      setErrors((prev) => ({
        ...prev,
        universityId: "",
      }));
    }
  };

  const handleUniversitySearchChange = (e) => {
    const value = e.target.value;
    setUniversitySearchTerm(value);
    setShowUniversityDropdown(true);

    if (!value.trim()) {
      setFilteredUniversities(universities);
      setFormData((prev) => ({
        ...prev,
        universityId: "",
        campusId: "",
        facultyId: "",
      }));
    }
  };

  const handleUniversitySearchFocus = () => {
    setShowUniversityDropdown(true);
  };

  const handleUniversitySearchBlur = () => {
    setTimeout(() => setShowUniversityDropdown(false), 200);
  };

  const handleCampusSelect = (campus) => {
    setFormData((prev) => ({
      ...prev,
      campusId: campus.id,
      facultyId: "",
    }));
    setCampusSearchTerm(campus.nameEn);
    setShowCampusDropdown(false);

    // Clear campus error if any
    if (errors.campusId) {
      setErrors((prev) => ({
        ...prev,
        campusId: "",
      }));
    }
  };

  const handleCampusSearchChange = (e) => {
    const value = e.target.value;
    setCampusSearchTerm(value);
    setShowCampusDropdown(true);

    if (!value.trim()) {
      setFilteredCampuses(campuses);
      setFormData((prev) => ({
        ...prev,
        campusId: "",
        facultyId: "",
      }));
    }
  };

  const handleCampusSearchFocus = () => {
    setShowCampusDropdown(true);
  };

  const handleCampusSearchBlur = () => {
    setTimeout(() => setShowCampusDropdown(false), 200);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "universityId") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        campusId: "", // Reset campus when university changes
        facultyId: "", // Reset faculty when university changes
      }));
    } else if (name === "campusId") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        facultyId: "", // Reset faculty when campus changes
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.universityId) {
      newErrors.universityId = "University is required";
    }

    if (!formData.campusId) {
      newErrors.campusId = "Campus is required";
    }

    if (!formData.facultyId) {
      newErrors.facultyId = "Faculty is required";
    }

    if (!formData.nameEn?.trim()) {
      newErrors.nameEn = "English name is required";
    }

    if (!formData.nameLocal?.trim()) {
      newErrors.nameLocal = "Local name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Convert IDs to numbers
      const submitData = {
        ...formData,
        universityId: formData.universityId,
        campusId: formData.campusId,
        facultyId: formData.facultyId,
        nameEn: formData.nameEn.trim(),
        nameLocal: formData.nameLocal.trim(),
      };

      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="department-form">
      <div className="row g-4">
        {/* University Selection */}
        <div className="col-md-4">
          <label className="form-label fw-bold">
            University <span className="text-danger">*</span>
          </label>
          <div className="position-relative">
            <div className="input-group">
              <input
                type="text"
                name="universityNameEn"
                className={`form-control ${
                  errors.universityId ? "is-invalid" : ""
                }`}
                placeholder="Search universities..."
                value={universitySearchTerm}
                onChange={handleUniversitySearchChange}
                onFocus={handleUniversitySearchFocus}
                onBlur={handleUniversitySearchBlur}
                disabled={loading || universityLoading}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() =>
                  setShowUniversityDropdown(!showUniversityDropdown)
                }
                disabled={loading || universityLoading}
              >
                <i className="fas fa-search"></i>
              </button>
            </div>

            {/* Hidden input for form validation */}
            <input
              type="hidden"
              name="universityId"
              value={formData.universityId}
            />

            {errors.universityId && (
              <div className="invalid-feedback">{errors.universityId}</div>
            )}
            {universityLoading && (
              <small className="text-muted">Loading universities...</small>
            )}

            {/* Dropdown for university selection */}
            {showUniversityDropdown && filteredUniversities.length > 0 && (
              <div className="university-dropdown">
                {filteredUniversities.map((university) => (
                  <button
                    key={university.id}
                    type="button"
                    className="university-dropdown-item"
                    onClick={() => handleUniversitySelect(university)}
                  >
                    <div className="university-name-en">
                      {university.nameEn}
                    </div>
                    <div className="university-name-local">
                      {university.nameLocal}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {showUniversityDropdown && filteredUniversities.length === 0 && (
              <div className="university-dropdown">
                <div className="university-dropdown-item text-muted">
                  No universities found
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Campus Selection */}
        <div className="col-md-4">
          <label className="form-label fw-bold">
            Campus <span className="text-danger">*</span>
          </label>
          <div className="position-relative">
            <div className="input-group">
              <input
                type="text"
                name="campusNameEn"
                className={`form-control ${
                  errors.campusId ? "is-invalid" : ""
                }`}
                placeholder="Search campuses..."
                value={campusSearchTerm}
                onChange={handleCampusSearchChange}
                onFocus={handleCampusSearchFocus}
                onBlur={handleCampusSearchBlur}
                disabled={loading || campusLoading || !formData.universityId}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setShowCampusDropdown(!showCampusDropdown)}
                disabled={loading || campusLoading || !formData.universityId}
              >
                <i className="fas fa-search"></i>
              </button>
            </div>

            {/* Hidden input for form validation */}
            <input type="hidden" name="campusId" value={formData.campusId} />

            {errors.campusId && (
              <div className="invalid-feedback">{errors.campusId}</div>
            )}
            {campusLoading && (
              <small className="text-muted">Loading campuses...</small>
            )}
            {!formData.universityId && (
              <small className="text-muted">
                Please select a university first
              </small>
            )}

            {/* Dropdown for campus selection */}
            {showCampusDropdown && filteredCampuses.length > 0 && (
              <div className="university-dropdown">
                {filteredCampuses.map((campus) => (
                  <button
                    key={campus.id}
                    type="button"
                    className="university-dropdown-item"
                    onClick={() => handleCampusSelect(campus)}
                  >
                    <div className="university-name-en">{campus.nameEn}</div>
                    <div className="university-name-local">
                      {campus.nameLocal}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {showCampusDropdown && filteredCampuses.length === 0 && (
              <div className="university-dropdown">
                <div className="university-dropdown-item text-muted">
                  No campuses found
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Faculty Selection */}
        <div className="col-md-4">
          <label className="form-label fw-bold">
            Faculty <span className="text-danger">*</span>
          </label>
          <select
            name="facultyId"
            className={`form-select ${errors.facultyId ? "is-invalid" : ""}`}
            value={formData.facultyId}
            onChange={handleInputChange}
            disabled={loading || facultyLoading || !formData.campusId}
          >
            <option value="">
              {!formData.campusId ? "Select Campus First" : "Select Faculty"}
            </option>
            {faculties.map((faculty) => (
              <option key={faculty.id} value={faculty.id}>
                {faculty.nameEn}
              </option>
            ))}
          </select>
          {errors.facultyId && (
            <div className="invalid-feedback">{errors.facultyId}</div>
          )}
          {facultyLoading && (
            <small className="text-muted">Loading faculties...</small>
          )}
          {!formData.campusId && (
            <small className="text-muted">Please select a campus first</small>
          )}
        </div>

        {/* English Name */}
        <div className="col-md-6">
          <label className="form-label fw-bold">
            Department Name (English) <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            name="nameEn"
            className={`form-control ${errors.nameEn ? "is-invalid" : ""}`}
            placeholder="e.g., Computer Science"
            value={formData.nameEn}
            onChange={handleInputChange}
            disabled={loading}
          />
          {errors.nameEn && (
            <div className="invalid-feedback">{errors.nameEn}</div>
          )}
        </div>

        {/* Local Name */}
        <div className="col-md-6">
          <label className="form-label fw-bold">
            Department Name (Local) <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            name="nameLocal"
            className={`form-control ${errors.nameLocal ? "is-invalid" : ""}`}
            placeholder="e.g., Bilgisayar Mühendisliği"
            value={formData.nameLocal}
            onChange={handleInputChange}
            disabled={loading}
          />
          {errors.nameLocal && (
            <div className="invalid-feedback">{errors.nameLocal}</div>
          )}
        </div>

        {/* Status */}
        <div className="col-12">
          <div className="form-check form-switch">
            <input
              type="checkbox"
              name="status"
              className="form-check-input"
              id="departmentStatus"
              checked={formData.status}
              onChange={handleInputChange}
              disabled={loading}
            />
            <label
              className="form-check-label fw-medium"
              htmlFor="departmentStatus"
            >
              <i
                className={`fas ${
                  formData.status
                    ? "fa-check-circle text-success"
                    : "fa-times-circle text-danger"
                } me-2`}
              ></i>
              {formData.status ? "Active Department" : "Inactive Department"}
            </label>
            <div className="form-text">
              {formData.status
                ? "This department is active and accepting new students."
                : "This department is inactive and not accepting new students."}
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={loading}
        >
          <i className="fas fa-times me-2"></i>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {department ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>
              <i
                className={`fas ${department ? "fa-save" : "fa-plus"} me-2`}
              ></i>
              {department ? "Update Department" : "Create Department"}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default DepartmentForm;
